import React, { useState } from "react"
import { navigate } from "gatsby"

import OtpField from "components/common/otp-field"
import Loader from "components/common/loader"
import useSessionStorage from "hooks/useSessionStorage"
import useTracking from "hooks/useTracking"
import { useUser } from "store/contexts/user"
import useRecaptcha from "../login/hooks/useRecaptcha"
import { Title, Text, CancelButton, ErrorText, ResendButton } from "./styles"

const EnterCode = ({ closeModal, onSuccess, from, redirectTo }) => {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [resendSuccessful, setResendSuccessful] = useState(false)
  const [showResendError, setShowResendError] = useState(false)

  const [signInEmail, setSignInEmail] = useSessionStorage("signInEmail")
  const [lastResendTime, setLastResendTime] = useSessionStorage("lrt") // last resend time
  const { verifyRecaptcha } = useRecaptcha()

  const { getUser } = useUser()
  const { track } = useTracking()

  const sendOtp = async (otp) => {
    if (!otp || !signInEmail) return

    setLoading(true)
    setError("")

    try {
      await verifyRecaptcha()
    } catch (err) {
      setError(`Recaptha error: ${err?.message}`)
      setLoading(false)
      return
    }

    const response = await fetch("/api/account/verify-passwordless-code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email: signInEmail, code: otp }),
    })

    if (!response.ok) {
      setLoading(false)
      const errorData = await response.json()
      setError(errorData.message || "Invalid code.")
      return
    }

    await getUser()

    await setSignInEmail("")

    track("User Logged In")

    setLoading(false)

    if (onSuccess) {
      onSuccess()
    }

    if (from === "page")
      if (redirectTo && redirectTo.startsWith("https")) {
        window.location.href = redirectTo
      } else {
        navigate(redirectTo || "/")
      }
  }

  const resendCode = async () => {
    if (!signInEmail) return

    if (lastResendTime && Date.now() - lastResendTime < 60000) {
      setShowResendError(true)
      setTimeout(() => {
        setShowResendError(false)
      }, 6000)
      return
    }

    setLoading(true)
    setError("")

    const response = await fetch("/api/account/login-passwordless", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email: signInEmail }),
    })

    if (!response.ok) {
      setLoading(false)
      const errorData = await response.json()
      setError(
        errorData.message ||
          "Something went wrong. We could not send you the email. Please try to sign in again.",
      )
      return
    }

    await setLastResendTime(Date.now())

    setResendSuccessful(true)
    setLoading(false)

    setTimeout(() => {
      setResendSuccessful(false)
    }, 3000)
  }

  return (
    <>
      <Title>Enter your code</Title>
      <Text>
        We just emailed {signInEmail} with a 6-digit code. If you don’t see it, please check your
        spam folder or{" "}
        <ResendButton onClick={resendCode} disabled={loading}>
          resend code
        </ResendButton>
        .
      </Text>
      {resendSuccessful && !showResendError && <Text>New code sent successfully!</Text>}
      {showResendError && <ErrorText>Please wait 60 seconds before resending the code.</ErrorText>}
      {loading && <Loader />}
      <OtpField onSuccess={sendOtp} disabled={loading} error={!!error} />
      <ErrorText>{error}</ErrorText>

      <CancelButton
        onClick={() => (from === "modal" ? closeModal() : navigate("/"))}
        disabled={loading}
      >
        Cancel
      </CancelButton>
    </>
  )
}
export default EnterCode
